import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react';
import {AnimatePresence} from 'framer-motion';
import CourtDiagram from '../components/CourtDiagram.jsx';
import Footer from '../components/Footer.jsx';
import GameOverModal from '../components/GameOverModal.jsx';
import PointTagSheet from '../components/PointTagSheet.jsx';
import ScoreLogBar from '../components/ScoreLogBar.jsx';
import ScoreLogDrawer from '../components/ScoreLogDrawer.jsx';
import ScorePanel from '../components/ScorePanel.jsx';
import StatsScreen from '../components/stats/StatsScreen.jsx';
import SwapNotification from '../components/SwapNotification.jsx';
import SweatToast from '../components/SweatToast.jsx';
import TopBar from '../components/TopBar.jsx';
import {useAutoSave} from '../hooks/useAutoSave.js';
import {useSwapNotification} from '../hooks/useSwapNotification.js';
import {useSweatReminder} from '../hooks/useSweatReminder.js';
import {
  getAlertLabel,
  makeInitState,
  matchReducer
} from '../logic/matchReducer.js';
import {
  playScoreSound,
  playUndoSound,
  playWinSound,
  vibrate
} from '../utils/audio.js';
import {loadSettings} from '../utils/storage.js';
import {releaseWakeLock, requestWakeLock} from '../utils/wakeLock.js';

const makeBus = () => ({
  listeners: new Map(),
  emit(key, value) {
    (this.listeners.get(key) || []).forEach(listener => listener(value));
  },
  on(key, listener) {
    if (!this.listeners.has(key)) this.listeners.set(key, []);
    this.listeners.get(key).push(listener);

    return () => {
      const listeners = this.listeners.get(key) || [];
      this.listeners.set(
        key,
        listeners.filter(item => item !== listener)
      );
    };
  }
});

const SwapFlash = ({trigger}) => {
  const [show, setShow] = useState(false);
  const previous = useRef(trigger);

  useEffect(() => {
    if (trigger === previous.current) return;

    previous.current = trigger;
    setShow(true);
    const timer = setTimeout(() => setShow(false), 500);
    return () => clearTimeout(timer);
  }, [trigger]);

  if (!show) return null;

  return (
    <div
      className="swap-flash fixed inset-0 z-[100] pointer-events-none"
      style={{background: 'rgba(251,191,36,0.12)'}}
    />
  );
};

const MatchScreen = ({initMatch, onReset, savedState}) => {
  const [state, dispatch] = useReducer(
    matchReducer,
    savedState || initMatch,
    savedState ? value => value : makeInitState
  );
  const [elapsed, setElapsed] = useState(0);
  const [showLog, setShowLog] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [pendingTag, setPendingTag] = useState(null);
  const [settings, setSettings] = useState(
    () => loadSettings() || {
      sweatEnabled: true,
      sweatInterval: 7,
      tagEnabled: true
    }
  );
  const bus = useMemo(makeBus, []);

  useAutoSave(state);

  useEffect(() => {
    requestWakeLock();
    return () => releaseWakeLock();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setElapsed(value => value + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const totalPoints = state.scoreA + state.scoreB;
  const [showSweat, dismissSweat] = useSweatReminder(
    totalPoints,
    settings.sweatInterval,
    settings.sweatEnabled
  );
  const [showSwap, swapReason, dismissSwap] = useSwapNotification(
    state.swapped,
    state.game,
    state.bestOf,
    state.pts
  );

  const previousState = useRef({
    scoreA: state.scoreA,
    scoreB: state.scoreB,
    historyLength: state.history.length
  });

  useEffect(() => {
    const previous = previousState.current;
    const isOver = ['game_over', 'match_over'].includes(state.status);
    const restarted = !state.history.length && previous.historyLength > 0;
    const undone = state.history.length < previous.historyLength;
    const scoreAChanged = state.scoreA !== previous.scoreA;
    const scoreBChanged = state.scoreB !== previous.scoreB;

    if (!restarted) {
      if (undone) {
        bus.emit('scoreA', state.scoreA);
        bus.emit('scoreB', state.scoreB);
        playUndoSound();
      } else {
        if (scoreAChanged) bus.emit('scoreA', state.scoreA);
        if (scoreBChanged) bus.emit('scoreB', state.scoreB);

        if ((scoreAChanged || scoreBChanged) && !isOver) {
          playScoreSound();
          vibrate([25]);
        }

        if (isOver) playWinSound();
      }
    }

    previousState.current = {
      scoreA: state.scoreA,
      scoreB: state.scoreB,
      historyLength: state.history.length
    };
  }, [
    bus,
    state.history.length,
    state.scoreA,
    state.scoreB,
    state.status
  ]);

  const handlePoint = useCallback(team => {
    dispatch({type: 'POINT', team});

    if (settings.tagEnabled) {
      setPendingTag({
        team,
        name: team === 'A' ? state.teamNameA : state.teamNameB
      });
    }
  }, [
    settings.tagEnabled,
    state.teamNameA,
    state.teamNameB
  ]);

  const tapLeft = useCallback(
    () => handlePoint(state.swapped ? 'B' : 'A'),
    [handlePoint, state.swapped]
  );
  const tapRight = useCallback(
    () => handlePoint(state.swapped ? 'A' : 'B'),
    [handlePoint, state.swapped]
  );

  const undo = useCallback(() => {
    dispatch({type: 'UNDO'});
    setPendingTag(null);
  }, []);

  const gamesNeeded = Math.ceil(state.bestOf / 2);
  const leftTeamId = state.swapped ? 'B' : 'A';
  const rightTeamId = state.swapped ? 'A' : 'B';

  const left = {
    teamId: leftTeamId,
    teamName: state.swapped ? state.teamNameB : state.teamNameA,
    playerName: state.swapped ? state.playerNameB : state.playerNameA,
    games: state.swapped ? state.gamesB : state.gamesA,
    score: state.swapped ? state.scoreB : state.scoreA,
    busKey: state.swapped ? 'scoreB' : 'scoreA'
  };

  const right = {
    teamId: rightTeamId,
    teamName: state.swapped ? state.teamNameA : state.teamNameB,
    playerName: state.swapped ? state.playerNameA : state.playerNameB,
    games: state.swapped ? state.gamesA : state.gamesB,
    score: state.swapped ? state.scoreA : state.scoreB,
    busKey: state.swapped ? 'scoreA' : 'scoreB'
  };

  const serverIsLeft = (
    state.serving === 'A' && !state.swapped
  ) || (
    state.serving === 'B' && state.swapped
  );
  const displayServeSide = serverIsLeft
    ? state.serveSide
    : state.serveSide === 'right' ? 'left' : 'right';
  const isOver = ['game_over', 'match_over'].includes(state.status);
  const alerts = getAlertLabel(
    state.scoreA,
    state.scoreB,
    state.gamesA,
    state.gamesB,
    state.pts,
    state.bestOf
  );
  const leftAlert = state.swapped ? alerts.alertB : alerts.alertA;
  const rightAlert = state.swapped ? alerts.alertA : alerts.alertB;
  const hasPointData = Boolean(
    state.pointLog?.length || state.allPointLogs?.length
  );

  return (
    <div className="h-[100dvh] w-screen flex flex-col overflow-hidden relative bg-[#020617]">
      <div className="fixed inset-0 pointer-events-none">
        <div className="blob blob-a" />
        <div className="blob blob-b" />
        <div className="grid-bg" />
      </div>

      <SwapFlash trigger={state.swapped} />
      <SweatToast show={showSweat} onDismiss={dismissSweat} />
      <SwapNotification
        show={showSwap}
        reason={swapReason}
        onDismiss={dismissSwap}
      />

      <TopBar
        elapsed={elapsed}
        game={state.game}
        gamesA={state.gamesA}
        gamesB={state.gamesB}
        gamesNeeded={gamesNeeded}
        canUndo={state.history.length > 0}
        onUndo={undo}
        onSwap={() => dispatch({type: 'SWAP'})}
        onReset={onReset}
        onShowLog={() => setShowLog(true)}
        pts={state.pts}
        bestOf={state.bestOf}
        teamNameA={state.teamNameA}
        teamNameB={state.teamNameB}
        swapped={state.swapped}
        settings={settings}
        onSettingsSave={setSettings}
        onLet={() => dispatch({type: 'LET'})}
        onDispute={data => dispatch({type: 'DISPUTE', ...data})}
        currentServer={state.serving}
        isPlaying={!isOver}
        onShowStats={() => setShowStats(true)}
      />

      <main className="flex-1 flex min-h-0 relative z-10 w-full">
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/[0.06] z-0 pointer-events-none -translate-x-1/2" />

        <ScorePanel
          key={`L-${left.busKey}-g${state.game}-sw${state.swapped}`}
          side="left"
          teamId={left.teamId}
          name={left.teamName}
          subName={left.playerName}
          isServing={state.serving === left.teamId}
          serveSide={displayServeSide}
          games={left.games}
          gamesNeeded={gamesNeeded}
          initScore={left.score}
          busKey={left.busKey}
          bus={bus}
          onTap={tapLeft}
          matchPointAlert={leftAlert}
        />

        <div
          className="absolute left-1/2 -translate-x-1/2 z-30 pointer-events-none flex flex-col items-center"
          style={{
            top: 'clamp(85px,18dvh,150px)',
            width: 'clamp(68px,18vw,100px)'
          }}
        >
          <div className="pointer-events-auto w-full">
            <CourtDiagram
              serving={state.serving}
              serveSide={state.serveSide}
              swapped={state.swapped}
              teamNameA={state.teamNameA}
              teamNameB={state.teamNameB}
            />
          </div>
        </div>

        <ScorePanel
          key={`R-${right.busKey}-g${state.game}-sw${state.swapped}`}
          side="right"
          teamId={right.teamId}
          name={right.teamName}
          subName={right.playerName}
          isServing={state.serving === right.teamId}
          serveSide={displayServeSide}
          games={right.games}
          gamesNeeded={gamesNeeded}
          initScore={right.score}
          busKey={right.busKey}
          bus={bus}
          onTap={tapRight}
          matchPointAlert={rightAlert}
        />
      </main>

      <ScoreLogBar
        log={state.log}
        onOpenFull={() => setShowLog(true)}
      />
      <Footer hideButton centered />

      <ScoreLogDrawer
        open={showLog}
        onClose={() => setShowLog(false)}
        log={state.log}
        canUndo={state.history.length > 0}
        canRedo={state.redoStack?.length > 0}
        onUndo={undo}
        onRedo={() => dispatch({type: 'REDO'})}
        onShowStats={() => setShowStats(true)}
        hasStats={hasPointData}
      />

      <StatsScreen
        open={showStats}
        onClose={() => setShowStats(false)}
        allPointLogs={[
          ...(state.allPointLogs ?? []),
          ...(state.pointLog?.length ? [state.pointLog] : [])
        ]}
        teamNameA={state.teamNameA}
        teamNameB={state.teamNameB}
        pts={state.pts}
        gamesA={state.gamesA}
        gamesB={state.gamesB}
        bestOf={state.bestOf}
      />

      <AnimatePresence>
        {isOver && (
          <GameOverModal
            state={state}
            onNextGame={() => dispatch({type: 'NEXT_GAME'})}
            onPlayAgain={() => dispatch({type: 'RESTART_MATCH'})}
            onReset={onReset}
          />
        )}
      </AnimatePresence>

      <PointTagSheet
        open={Boolean(pendingTag) && !isOver}
        scorerTeam={pendingTag?.team}
        scorerName={pendingTag?.name}
        onTag={tagId => {
          dispatch({type: 'TAG_POINT', tagId});
          setPendingTag(null);
        }}
        onSkip={() => setPendingTag(null)}
      />
    </div>
  );
};

export default MatchScreen;